import axios from 'axios'
import hash from 'object-hash'

import { chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

const SUPERIOR_CHARTS = [
  'alunos-matriculados',
  'alunos-ingressantes',
  'alunos-formados',
  'alunos-faixa-etaria',
  'alunos-turno',
  'alunos-grau',
  'alunos-churn',
  'alunos-fies',
  'alunos-prouni',
  'alunos-out',
] as const

type CourseType = 'presencial' | 'ead'
type SuperiorBarChartType = typeof SUPERIOR_CHARTS[number]

const EDUCATION_CHART_URL = '/api/charts/education/superior'
const EDUCATION_REQUEST_TIMEOUT_MS = 120000

export interface ChartData extends CompassAPIDataBase {
  title: string
  data: {
    name: string
    id: string
    values: { label: string; value: number }[]
  }[]
}

interface GroupOptions {
  title?: string
  group: { year: number; list_ies: number[] }[]
  categoriaAdmin?: number[]
}

export namespace Education {
  export namespace SuperiorBar {
    const CONTEXT_ID = 'equity:education:superior_bar'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends GroupOptions {
      course: CourseType
      chartType: SuperiorBarChartType
      addFilter: boolean
      isPercentage: boolean
      showIds: string[]
      context: typeof CONTEXT_ID
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: EDUCATION_CHART_URL + '/' + config.chartType,
        method: 'POST',
        data: {
          group: config.group,
          categoria_admin: config.categoriaAdmin,
          course_type: config.course,
        },
        timeout: EDUCATION_REQUEST_TIMEOUT_MS,
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const CRESCIMENTO_CHARTS = [
        'alunos-matriculados',
        'alunos-ingressantes',
        'alunos-formados',
      ]

      const getChartOpacity = (name: string, chartType: SuperiorBarChartType) =>
        name !== 'crescimento' && CRESCIMENTO_CHARTS.includes(chartType)
          ? 0.5
          : 1

      const filteredData = config.addFilter
        ? data.data.filter(({ id }) => config.showIds.includes(id))
        : data.data

      const series = filteredData.map((s, idx) => {
        const name = s.name.toLowerCase()
        const [type, yAxis] =
          name === 'crescimento'
            ? ['line', 'variacao']
            : ['column', 'quantidade']
        return {
          id: s.id,
          name: s.name,
          color: config.addFilter ? chartPalette[idx] : chartPalette[0],
          opacity: getChartOpacity(name, config.chartType),
          data: s.values.map(({ label, value }) => [label, value]),
          marker: { enabled: true },
          tooltip: {
            pointFormatter: magnitudePointFormatter(),
            valueDecimals: 2,
          },
          type,
          yAxis,
        }
      })

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = CRESCIMENTO_CHARTS.includes(config.chartType)
        ? [
            {
              id: 'quantidade',
              opposite: false,
              title: { text: 'Quantidade de alunos' },
            },
            {
              id: 'variacao',
              opposite: true,
              title: { text: 'Crescimento YoY' },
            },
          ]
        : [
            {
              id: 'quantidade',
              opposite: false,
              title: { text: '' },
            },
          ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        height: config.addFilter ? '360px' : '400px',
        options: mergeDeepAll([
          creditsConfig,
          {
            series,
            yAxis,
            xAxis: [{ type: 'category', title: { text: 'Ano' } }],
            legend: { enabled: series && series.length > 1 },
            plotOptions: {
              column: {
                dataLabels: {
                  enabled: config.isPercentage,
                  format: '{y:.2f}%',
                },
              },
            },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace SuperiorSliderSeries {
    const CONTEXT_ID = 'equity:education:superior_slider_series'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends GroupOptions {
      course: CourseType
      cities: string[]
      states: string[]
      regions: string[]
      field: string
      useCache: boolean
      shareMinimo: number
      context: typeof CONTEXT_ID
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: `${EDUCATION_CHART_URL}/historical-marketshare`,
        method: 'POST',
        data: {
          categoria_admin: config.categoriaAdmin,
          group: config.group,
          cities: config.cities,
          states: config.states,
          regions: config.regions,
          field: config.field,
          use_cache: config.useCache,
          course_type: config.course,
        },
        timeout: EDUCATION_REQUEST_TIMEOUT_MS,
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const filteredData = data.data.filter(({ values }) =>
        values.some((point) => point.value >= config.shareMinimo)
      )
      const series = filteredData.map((s) => ({
        data: s.values.map(({ label, value }) => [
          label.replaceAll('.0', ''),
          value,
        ]),
        id: s.name,
        marker: { enabled: true },
        name: s.name,
        tooltip: {
          xDateFormat: '%m/%Y',
          valueDecimals: 2,
        },
        type: 'line',
        yAxis: 'default',
        dataLabels: { enabled: true, format: '{y:.2f}%' },
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: '{value}%' },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        height: 346,
        options: mergeDeepAll([
          creditsConfig,
          {
            colors: chartPalette,
            series,
            yAxis,
            xAxis: [{ type: 'category', title: { text: 'Ano' } }],
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }
  export namespace Valuation {
    const CONTEXT_ID = 'equity:education:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'pe_yduq3'
        | 'pe_cogn3'
        | 'pe_anim3'
        | 'pe_seer3'
        | 'pe_csed3'
        | 'pe_vtru3'
        | 'pe_laur3'
        | 'pe_arce_us'
        | 'pe_vsta_us'
        | 'pe_afya_us'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getValuationChartProps(config, data),
    }

    const getValuationChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)
  }
}
